/**
 * QuotationService
 * Service for Quotation entity operations
 * Route: api/quotation
 * Controller: QuotationController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * QuotationService
 * Handles quotation CRUD operations and custom endpoints
 * Maps to /api/quotation endpoint
 */
export class QuotationService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? QuotationService.DEFAULT_SERVICE_PATH,
            basePath: QuotationService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Get quotation with enriched model view by ID
     * GET /api/quotation/view/{id}
     * @param id - Quotation ID
     * @returns Enriched quotation model view
     */
    async getViewById(id) {
        const response = await this.getViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`Quotation view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get quotation view by ID (raw)
     * @param id - Quotation ID
     * @returns ResponseData with enriched model view
     */
    getViewByIdRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all quotation views (enriched with lookup data)
     * GET /api/quotation/view
     * @returns Array of enriched quotation model views
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all quotation views (raw)
     * @returns ResponseData with array of enriched model views
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('allViews', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get quotation views by purchase request ID
     * GET /api/quotation/request/{requestId}/view
     * @param requestId - Purchase request ID
     * @returns Array of enriched quotation model views for the request
     */
    async getViewsByRequest(requestId) {
        const response = await this.getViewsByRequestRaw(requestId);
        return response.data ?? [];
    }
    /**
     * Get quotation views by request (raw)
     * @param requestId - Purchase request ID
     * @returns ResponseData with array of enriched model views
     */
    getViewsByRequestRaw(requestId) {
        const endpoint = this.getEndpoint('viewsByRequest', `request/${requestId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get filtered list of quotation views (enriched with lookup data)
     * POST /api/quotation/list-view
     * @param filter - Optional filter criteria
     * @returns Array of enriched quotation model views
     */
    async getListView(filter) {
        const response = await this.getListViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get filtered list of quotation views (raw)
     * @param filter - Optional filter criteria
     * @returns ResponseData with array of enriched model views
     */
    getListViewRaw(filter) {
        const endpoint = this.getEndpoint('listView', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || {});
    }
}
QuotationService.DEFAULT_SERVICE_PATH = 'procurements';
QuotationService.BASE_PATH = 'api/quotation';
//# sourceMappingURL=QuotationService.js.map